import { nextTick, watch, type Ref } from 'vue';
import type { CategoryCollection, EntityDocument, EntityRevision, EntityVersion, TiptapDoc } from '../types/entities';
import type { TimelineOccurrence, TimelineOccurrenceDraft } from '../types/timeline';
import type { WorkspaceSaveState } from '../types/workspace';
import type { useWorkspaceStore } from '../stores/workspace';

export function useEntityLifecycle(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  draft: { title: string; summary: string; contentDoc: TiptapDoc; fieldValues: Record<string, unknown> };
  timelineDraft: TimelineOccurrenceDraft;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  timelineOccurrences: Readonly<Ref<TimelineOccurrence[]>>;
  categoryCollections: Readonly<Ref<CategoryCollection[]>>;
  isHistoricalVersion: Readonly<Ref<boolean>>;
  isVersionReadonly: Readonly<Ref<boolean>>;
  isWorkspaceReadonly: Readonly<Ref<boolean>>;
  isScene: Readonly<Ref<boolean>>;
  sceneChapterInfo: Readonly<Ref<Array<{ id: string; title: string; position: number }>>>;
  displayedEntityVersion: Readonly<Ref<EntityVersion | null>>;
  displayedEntityRevision: Readonly<Ref<EntityRevision | null>>;
  displayedEntityVersionId: Ref<string>;
  displayedEntityRevisionId: Ref<string | null>;
  activeEntityCollectionIds: Ref<string[]>;
  sceneConflictsMinimized: Ref<boolean>;
  profileImageSaving: Ref<boolean>;
  attachmentUploading: Ref<boolean>;
  attachmentError: Ref<string>;
  saveState: Readonly<Ref<WorkspaceSaveState>>;
  saveTimer: Ref<ReturnType<typeof setTimeout> | undefined>;
  saveInFlight: Ref<Promise<void> | null>;
  draftChangeId: Ref<number>;
  sceneEditorRef: Readonly<Ref<{ flush: () => Promise<void> } | null>>;
}) {
  const { store, draft, timelineDraft, activeEntity, timelineOccurrences, categoryCollections, isHistoricalVersion, isVersionReadonly, isWorkspaceReadonly, isScene, sceneChapterInfo, displayedEntityVersion, displayedEntityRevision, displayedEntityVersionId, displayedEntityRevisionId, activeEntityCollectionIds, sceneConflictsMinimized, profileImageSaving, attachmentUploading, attachmentError, saveState, saveTimer, saveInFlight, draftChangeId, sceneEditorRef } = options;
function hydrateDraft() {
  const entity = activeEntity.value;
  if (!entity) return;
  const snapshot = isHistoricalVersion.value
    ? displayedEntityRevision.value ?? displayedEntityVersion.value ?? entity.workingVersion
    : entity.workingVersion;
  if (!snapshot.contentDoc || !snapshot.fieldValues) return;
  draft.title = snapshot.title;
  draft.summary = snapshot.summary ?? '';
  draft.contentDoc = JSON.parse(JSON.stringify(snapshot.contentDoc)) as TiptapDoc;
  draft.fieldValues = JSON.parse(JSON.stringify(snapshot.fieldValues)) as Record<string, unknown>;
}

function hydrateTimelineDraft() {
  const occurrence = timelineOccurrences.value.find((item) => item.entityId === activeEntity.value?.id);
  Object.assign(timelineDraft, occurrence ? {
    eraEntityId: occurrence.eraEntityId, inUniverseStart: occurrence.inUniverseStart ?? '', inUniverseEnd: occurrence.inUniverseEnd ?? '',
    sortKey: occurrence.sortKey, narrativePosition: occurrence.narrativePosition, precision: occurrence.precision,
    isUncertain: occurrence.isUncertain, notes: occurrence.notes ?? '', calendarYear: occurrence.calendarYear, calendarPeriod: occurrence.calendarPeriod, calendarDay: occurrence.calendarDay, calendarParts: { ...occurrence.calendarParts }, clockLabel: occurrence.clockLabel ?? '', timeBasis: occurrence.timeBasis ?? 'calendar', absoluteTime: occurrence.absoluteTime, timeSource: occurrence.timeSource, narrativeRelation: occurrence.narrativeRelation, relativeAnchor: occurrence.relativeAnchor ?? null, relativeOffsetMinutes: occurrence.relativeOffsetMinutes
  } : { eraEntityId: null, inUniverseStart: '', inUniverseEnd: '', sortKey: 0, narrativePosition: null, precision: 'exact', isUncertain: false, notes: '', calendarYear: null, calendarPeriod: null, calendarDay: null, calendarParts: {}, clockLabel: '', timeBasis: 'calendar', absoluteTime: null, timeSource: isScene.value && sceneChapterInfo.value.length ? 'inherit' : 'explicit', narrativeRelation: 'normal', relativeAnchor: null, relativeOffsetMinutes: null });
}
watch(activeEntity, async () => {
  clearTimeout(saveTimer.value);
  sceneConflictsMinimized.value = false;
  displayedEntityVersionId.value = activeEntity.value?.workingVersion.id ?? '';
  displayedEntityRevisionId.value = null;
  await nextTick();
  hydrateDraft();
  hydrateTimelineDraft();
  activeEntityCollectionIds.value = activeEntity.value ? categoryCollections.value.filter((collection) => collection.entityIds.includes(activeEntity.value!.id)).map((collection) => collection.id) : [];
});
watch(timelineOccurrences, hydrateTimelineDraft);

function scheduleSave() {
  if (isVersionReadonly.value) return;
  draftChangeId.value += 1;
  store.markDirty();
  clearTimeout(saveTimer.value);
  saveTimer.value = setTimeout(() => void flushSave().catch(() => undefined), 750);
}
async function saveProfileImage(imageData: string | null) {
  if (!activeEntity.value || isWorkspaceReadonly.value) return;
  profileImageSaving.value = true;
  try { await store.setEntityProfileImage(activeEntity.value.id, imageData); }
  catch (cause) { window.alert(cause instanceof Error ? cause.message : 'Profilbild konnte nicht gespeichert werden.'); }
  finally { profileImageSaving.value = false; }
}
async function uploadAttachments(files: File[]) {
  if (!activeEntity.value || isWorkspaceReadonly.value || !files.length) return;
  attachmentUploading.value = true;
  attachmentError.value = '';
  try {
    for (const file of files) await store.uploadEntityAttachment(activeEntity.value.id, file);
  } catch (cause) {
    attachmentError.value = cause instanceof Error ? cause.message : 'Datei konnte nicht angehängt werden.';
  } finally { attachmentUploading.value = false; }
}
async function removeAttachment(attachment: { id: string; originalName: string }) {
  if (isWorkspaceReadonly.value || !window.confirm(`„${attachment.originalName}“ wirklich entfernen?`)) return;
  attachmentUploading.value = true;
  attachmentError.value = '';
  try { await store.removeEntityAttachment(attachment.id); }
  catch (cause) { attachmentError.value = cause instanceof Error ? cause.message : 'Datei konnte nicht entfernt werden.'; }
  finally { attachmentUploading.value = false; }
}

async function flushSave() {
  clearTimeout(saveTimer.value);
  if (saveInFlight.value) await saveInFlight.value;
  await sceneEditorRef.value?.flush();
  if (!activeEntity.value || !['dirty', 'offline'].includes(saveState.value)) return;
  const changeId = draftChangeId.value;
  const save = store.saveDraft({
    title: draft.title,
    summary: draft.summary.trim() || null,
    contentDoc: JSON.parse(JSON.stringify(draft.contentDoc)) as TiptapDoc,
    fieldValues: JSON.parse(JSON.stringify(draft.fieldValues)) as Record<string, unknown>
  }).then(() => undefined);
  saveInFlight.value = save;
  try { await save; }
  finally { if (saveInFlight.value === save) saveInFlight.value = null; }
  if (draftChangeId.value !== changeId) {
    store.markDirty();
    await flushSave();
  }
}

let versionBrowseGeneration = 0;
async function browseEntityVersion(versionId: string, revisionId: string | null) {
  if (!activeEntity.value) return;
  const generation = ++versionBrowseGeneration;
  const entity = activeEntity.value;
  if (!isVersionReadonly.value) {
    await flushSave();
    await store.checkpointActive().catch(() => undefined);
  }
  if (generation !== versionBrowseGeneration || activeEntity.value !== entity) return;
  displayedEntityVersionId.value = versionId;
  displayedEntityRevisionId.value = revisionId;
  if (versionId !== entity.workingVersion.id || revisionId) await store.loadEntityVersionSnapshot(versionId, revisionId);
  if (generation !== versionBrowseGeneration || activeEntity.value !== entity
    || displayedEntityVersionId.value !== versionId || displayedEntityRevisionId.value !== revisionId) return;
  hydrateDraft();
  if (isScene.value) await store.loadSceneSegments(activeEntity.value.id, versionId, revisionId);
}

async function entityVersionsChanged() {
  if (!activeEntity.value) return;
  displayedEntityVersionId.value = activeEntity.value.workingVersion.id;
  displayedEntityRevisionId.value = null;
  hydrateDraft();
  if (isScene.value) await store.loadSceneSegments(activeEntity.value.id, activeEntity.value.workingVersion.id);
}

  return { hydrateDraft, hydrateTimelineDraft, scheduleSave, saveProfileImage, uploadAttachments, removeAttachment, flushSave, browseEntityVersion, entityVersionsChanged };
}
